import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { handleApiError, queryParams } from '../services/api';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { Search, ChevronLeft, ChevronRight, Package, AlertTriangle, ArrowUpDown, Eye } from 'lucide-react';

export default function StockPage() {
  const navigate = useNavigate();
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', warehouse_id: '', category_id: '' });
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchStocks();
  }, [pagination.page, filters]);

  useEffect(() => {
    fetchLookups();
  }, []);

  const fetchStocks = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/inventory/stock', { params });
      if (res.data.success) {
        setStocks(res.data.data?.stocks || res.data.data?.items || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLookups = async () => {
    try {
      const [whRes, catRes] = await Promise.all([
        api.get('/warehouses', { params: { limit: 1000 } }),
        api.get('/categories', { params: { limit: 1000 } }),
      ]);
      setWarehouses(whRes.data.data?.warehouses || whRes.data.data || []);
      setCategories(catRes.data.data?.categories || catRes.data.data || []);
    } catch (error) {
      // Silently fail
    }
  };

  const isLowStock = (item) => {
    return item.quantity != null && item.min_stock != null && item.quantity <= item.min_stock;
  };

  const totalValue = stocks.reduce((sum, s) => sum + (s.total_value || (s.quantity || 0) * (s.average_price || 0)), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Saldo Stok</h1>
          <p className="page-subtitle">Lihat tingkat stok saat ini untuk semua produk</p>
        </div>
        <button onClick={() => fetchStocks()} className="btn-secondary btn-sm">
          <ArrowUpDown className="h-4 w-4 mr-2" /> Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="stat-card">
          <div className="stat-icon bg-primary-50 text-primary-900 ring-1 ring-primary-100 dark:bg-primary-950 dark:text-primary-300 dark:ring-primary-900">
            <Package size={20} />
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-surface-500 dark:text-surface-400">Total Item Stok</p>
            <p className="mt-1.5 text-xl font-medium tracking-tight text-surface-900 tabular-nums dark:text-white">{formatNumber(stocks.length)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon bg-primary-50 text-primary-900 ring-1 ring-primary-100 dark:bg-primary-950 dark:text-primary-300 dark:ring-primary-900">
            <Package size={20} />
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-surface-500 dark:text-surface-400">Total Nilai Stok</p>
            <p className="mt-1.5 text-xl font-medium tracking-tight text-surface-900 tabular-nums dark:text-white">{formatCurrency(totalValue)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon bg-danger-50 text-danger-700 ring-1 ring-danger-100 dark:bg-danger-900/25 dark:text-danger-400 dark:ring-danger-900/60">
            <AlertTriangle size={20} />
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-surface-500 dark:text-surface-400">Stok Rendah</p>
            <p className="mt-1.5 text-xl font-medium tracking-tight text-surface-900 tabular-nums dark:text-white">{formatNumber(stocks.filter(isLowStock).length)}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari nama produk atau SKU..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchStocks()}
              />
            </div>
            <div>
              <select
                className="input"
                value={filters.warehouse_id}
                onChange={(e) => setFilters({ ...filters, warehouse_id: e.target.value })}
              >
                <option value="">Semua Gudang</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
            <div>
              <select
                className="input"
                value={filters.category_id}
                onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}
              >
                <option value="">Semua Kategori</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={fetchStocks} className="btn-primary btn-sm">Cari</button>
              <button onClick={() => setFilters({ search: '', warehouse_id: '', category_id: '' })} className="btn-secondary btn-sm">Reset</button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-40" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-16" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        ) : stocks.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data Stok</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada data saldo stok yang tersedia.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Nama Produk</th>
                    <th>Kategori</th>
                    <th>Gudang</th>
                    <th>Lokasi</th>
                    <th className="text-right">Jumlah</th>
                    <th className="text-right">Harga Rata-rata</th>
                    <th className="text-right">Total Nilai</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {stocks.map((stock, idx) => (
                    <tr key={stock.id || idx}>
                      <td>
                        <span className="font-mono text-xs text-gray-600 dark:text-gray-400">{stock.sku || '-'}</span>
                      </td>
                      <td>
                        <span className="font-medium text-gray-900 dark:text-white">{stock.product_name || stock.name || '-'}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-700 dark:text-gray-200">{stock.category_name || stock.category?.name || '-'}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-700 dark:text-gray-200">{stock.warehouse_name || stock.warehouse?.name || '-'}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-500 dark:text-gray-400">{stock.location || stock.location_name || '-'}</span>
                      </td>
                      <td className="text-right">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{formatNumber(stock.quantity)}</span>
                      </td>
                      <td className="text-right">
                        <span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(stock.average_price)}</span>
                      </td>
                      <td className="text-right">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatCurrency(stock.total_value || (stock.quantity || 0) * (stock.average_price || 0))}
                        </span>
                      </td>
                      <td>
                        {isLowStock(stock) ? (
                          <span className="badge badge-danger">
                            <AlertTriangle className="h-3 w-3 mr-1" /> Stok Rendah
                          </span>
                        ) : (
                          <span className="badge badge-success">Normal</span>
                        )}
                      </td>
                      <td>
                        <button
                          onClick={() => navigate(`/inventory/stock-card?product_id=${stock.product_id || stock.id}`)}
                          className="p-1.5 text-primary-900 hover:bg-primary-50 dark:hover:bg-primary-950/50 rounded"
                          title="Lihat Kartu Stok"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
